/**
 * Pure SQL string builders for the MERGE / SPLIT PARTITIONS script templates
 * (PostgreSQL 19+ only DDL — see PG19-FACTS c1/c2/c3 in
 * Docs/Pg19-Support-Plan.md for the exact accepted syntax this module
 * emits). No side effects; consumed by partitionContextMenuService.ts.
 */

/** A schema-qualified pgsql object reference, quoted at emit time. */
export type QualifiedName = { schema: string; name: string };

function q(ident: string): string {
  return `"${ident.replace(/"/g, '""')}"`;
}

function qualify(ref: QualifiedName): string {
  return `${q(ref.schema)}.${q(ref.name)}`;
}

function pgVersionWarning(pgMajor: number | null): string {
  return pgMajor !== null && pgMajor >= 19
    ? ''
    : `-- NOTE: MERGE/SPLIT PARTITIONS require PostgreSQL 19+ (this server reports ${pgMajor ?? 'an unknown version'}).\n`;
}

/**
 * SPLIT PARTITION template (PG19-FACTS c2/c3). The engine needs the two new
 * halves' `FOR VALUES` bounds, which this tool cannot infer from the parent's
 * partition strategy alone (Checkpoint-3 amendment #2) — they're left as
 * `TODO`-marked placeholders alongside the partition's current bound (echoed
 * as a comment) for the user to fill in before running the script.
 */
export function buildSplitPartitionTemplate(
  parent: QualifiedName,
  partition: QualifiedName,
  bound: string,
  pgMajor: number | null,
): string {
  const halfA: QualifiedName = { schema: partition.schema, name: `${partition.name}_a` };
  const halfB: QualifiedName = { schema: partition.schema, name: `${partition.name}_b` };
  const boundComment = bound ? `-- Current bound: ${bound}\n` : '';
  return (
    `${pgVersionWarning(pgMajor)}${boundComment}` +
    `-- TODO: pick a split point and set the FOR VALUES bound for each half below.\n` +
    `ALTER TABLE ${qualify(parent)} SPLIT PARTITION ${qualify(partition)} INTO (\n` +
    `  PARTITION ${qualify(halfA)} FOR VALUES FROM (/* TODO */) TO (/* TODO: split point */), -- TODO: lower half bound\n` +
    `  PARTITION ${qualify(halfB)} FOR VALUES FROM (/* TODO: split point */) TO (/* TODO */) -- TODO: upper half bound\n` +
    `);\n`
  );
}

/**
 * MERGE PARTITIONS template (PG19-FACTS c1/c3) — merges every current
 * sibling partition (Checkpoint-3 amendment #3: caller fetches the sibling
 * list fresh via `getPartitions()`, never stale `folder.children`) into one
 * new partition. No `FOR VALUES` clause is needed — PG19 computes the merged
 * bound from its sources — so this template is executable as-is.
 */
export function buildMergePartitionsTemplate(
  parent: QualifiedName,
  partitions: QualifiedName[],
  pgMajor: number | null,
): string {
  const list = partitions.map(qualify).join(', ');
  const merged: QualifiedName = { schema: parent.schema, name: `${parent.name}_merged` };
  return `${pgVersionWarning(pgMajor)}ALTER TABLE ${qualify(parent)} MERGE PARTITIONS (${list}) INTO ${qualify(merged)};\n`;
}
